import { useMemo } from "react";
import {
    keepPreviousData,
    queryOptions,
    useQuery,
    useMutation,
    useQueryClient,
    type UseQueryResult,
    type UseMutationResult,
} from "@tanstack/react-query";
import { fetchCampaigns, deleteCampaign, getTrashCampaign, restoreTrashedCampaign } from "./api";
import {
    ICampaign,
    ICampaignListParams,
    ICampaignListResult,
    ITrashCampaign,
} from "./types";

function cleanParams(p: ICampaignListParams): ICampaignListParams {
    const entries = Object.entries(p).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
    );
    return Object.freeze(Object.fromEntries(entries)) as ICampaignListParams;
}

/* ------------------- HISTORY CAMPAIGNS ------------------- */

export const historyCampaignsKey = (params: ICampaignListParams) =>
    ["historyCampaigns", cleanParams(params)] as const;

export const historyCampaignsQuery = <T extends ICampaign = ICampaign>(
    params: ICampaignListParams
) =>
    queryOptions<ICampaignListResult<T>>({
        queryKey: historyCampaignsKey(params),
        queryFn: () => fetchCampaigns<T>(params),
        placeholderData: keepPreviousData,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });

export function useHistoryCampaigns<T extends ICampaign = ICampaign>(
    params: ICampaignListParams,
    opts?: { enabled?: boolean }
): UseQueryResult<ICampaignListResult<T>> {
    const stable = useMemo(() => cleanParams(params), [JSON.stringify(params)]);

    return useQuery<ICampaignListResult<T>>({
        ...historyCampaignsQuery<T>(stable),
        enabled: opts?.enabled ?? true,
    });
}

/* ------------------- SIMPLE CAMPAIGNS ------------------- */

export const campaignsKey = (params: ICampaignListParams) =>
    ["campaigns", cleanParams(params)] as const;

export const campaignsQuery = <T extends ICampaign = ICampaign>(
    params: ICampaignListParams
) =>
    queryOptions<ICampaignListResult<T>>({
        queryKey: campaignsKey(params),
        queryFn: () => fetchCampaigns<T>(params),
        placeholderData: keepPreviousData,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });

export function useCampaigns<T extends ICampaign = ICampaign>(
    params: ICampaignListParams,
    opts?: { enabled?: boolean }
): UseQueryResult<ICampaignListResult<T>> {
    const stable = useMemo(() => cleanParams(params), [JSON.stringify(params)]);

    return useQuery<ICampaignListResult<T>>({
        ...campaignsQuery<T>(stable),
        enabled: opts?.enabled ?? true,
    });
}

/* ------------------- DELETE CAMPAIGN ------------------- */

export function useDeleteCampaign(): UseMutationResult<any, Error, number | string> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number | string) => deleteCampaign(id),
        onSuccess: () => {
            // Invalidate all campaign queries to refetch the updated list
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            queryClient.invalidateQueries({ queryKey: ["historyCampaigns"] });
        },
    });
}

/* ------------------- CAMPAIGN TRASH ------------------- */

/** Query: List all soft-deleted campaigns (GET /campaign-trash) */
export function useTrashCampaigns(): UseQueryResult<ITrashCampaign[]> {
    return useQuery({
        queryKey: ["campaignTrash"],
        queryFn: getTrashCampaign,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
}

/** Mutation: Restore a soft-deleted campaign (PUT /campaign-trash/{cid}) */
export function useRestoreTrashedCampaign(): UseMutationResult<any, Error, number | string> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number | string) => restoreTrashedCampaign(id),
        onSuccess: () => {
            // Refresh both the trash list and the live campaign lists
            queryClient.invalidateQueries({ queryKey: ["campaignTrash"] });
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            queryClient.invalidateQueries({ queryKey: ["historyCampaigns"] });
        },
    });
}
